/**
 * A directive that turns a textarea into a WYSIWYG text editor.
 * A more reliable implementation of the angular-trix adapter for the Trix Editor.
 * Based on http://sachinchoolur.github.io/angular-trix/
 * Usage: <textarea apex-wysiwyg-editor ng-model="$ctrl.model"></textarea>
 */
export function TrixWysiwygDirective() {
    'ngInject'
    const directive = {
        restrict: "A",
        require: "ngModel",
        scope: {
            // Override ngModel events:
            ngChange: '&',
            ngFocus: '&',
            ngBlur: '&',
            trixInitialize: '&',
            trixSelectionChange: '&',
            trixFileAccept: '&',
            trixAttachmentAdd: '&',
            trixAttachmentRemove: '&'
        },
        link,
        // Applying the directive directly to a trix-editor element doesn't work
        // because the trix-editor strips all of the attributes off the editor element
        // and puts them on the toolbar. This breaks ngModel integration. However,
        // this can be worked around by replacing the textarea with the trix-editor in the template.
        template: `<trix-editor class='trix-content'></trix-editor>`,
        replace: true
    }

    // Map from scope events to trix events.
    const events = [{
        // These events are extensions of the angular textarea HTML events, so use ng namespace.
        trixEvent: "trix-change",
        ngEvent: "ngChange"
    }, {
        trixEvent: "trix-focus",
        ngEvent: "ngFocus"
    }, {
        trixEvent: "trix-blur",
        ngEvent: "ngBlur"
    }, {
        // Also apply trix-specific events.
        trixEvent: "trix-initialize",
        ngEvent: "trixInitialize"
    }, {
        trixEvent: "trix-selection-change",
        ngEvent: "trixSelectionChange"
    }, {
        trixEvent: "trix-file-accept",
        ngEvent: "trixFileAccept"
    }, {
        trixEvent: "trix-attachment-add",
        ngEvent: "trixAttachmentAdd"
    }, {
        trixEvent: "trix-attachment-remove",
        ngEvent: "trixAttachmentRemove"
    }]

    return directive;

    function link(scope, el, attrs, ngModel) {
        // Trix performs its own DOM manipulation, so we may be linking to the toolbar
        // instead of the editor itself. Re-assert the original editor element.
        const trixEditorEl = el.siblings("trix-editor")[0] || el[0]

        for (const { trixEvent: type, ngEvent: method } of events) {
            trixEditorEl.addEventListener(type, function (e) {
                if (type === 'trix-file-accept' && attrs.preventTrixFileAccept === 'true') {
                    e.preventDefault();
                }

                scope[method]({
                    e: e,
                    editor: trixEditorEl.editor
                });
            });
        }

        if (!ngModel) return

        // Wait until the component has initialized before setting up the ngModel pipeline.
        ngModel.$formatters.push(function (modelValue) {
            trixEditorEl.editor.loadHTML(modelValue);
            return trixEditorEl.innerHTML
        })

        ngModel.$parsers.push(function (viewValue) {
            return viewValue
        })

        trixEditorEl.addEventListener("trix-initialize", function () {
            ngModel.render = function () {
                if (ngModel.$modelValue) {
                    trixEditorEl.editor.loadHTML(ngModel.$modelValue);
                }
            }
            ngModel.render()
            // v1.6+ only. For now, avoid wrapping the editor in ng-if while data is loading.
            // ngModel.$processModelValue();

        })

        trixEditorEl.addEventListener("trix-change", function () {
            ngModel.$setViewValue(trixEditorEl.innerHTML)
        })
    }
}
